import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import './App.css';

// User Auth
import { getCurrentUser, logoutUser, cleanupExpiredUsers, isAdmin } from './utils/userAuth';

// Components
import Navbar from './components/Navbar';
import MobileNav from './components/MobileNav';
import InstallPrompt from './components/InstallPrompt';
import AgenticAIChat from './components/AgenticAIChat';
import NetworkAccess from './components/NetworkAccess';

// Pages
import CareemHome from './pages/CareemHome';
import NewHome from './pages/NewHome';
import Login from './pages/Login';
import Register from './pages/Register';
import VerifyMagicLink from './pages/VerifyMagicLink';
import Dashboard from './pages/Dashboard';
import ServiceList from './pages/ServiceList';
import ServiceDetails from './pages/ServiceDetails';
import CreateService from './pages/CreateService';
import MyBookings from './pages/MyBookings';
import CollegeZone from './pages/CollegeZone';
import CreateProfile from './pages/CreateProfile';
import Showcase from './pages/Showcase';
import Marketplace from './pages/Marketplace';
import Rentals from './pages/Rentals';
import RidePooling from './pages/RidePooling';
import Rides from './pages/Rides';
import OfferRide from './pages/OfferRide';
import OnTapFeed from './pages/OnTapFeed';
import StudentTools from './pages/StudentTools';
import ResumeOptimizer from './pages/ResumeOptimizer';
import AutoApply from './pages/AutoApply';
import MindWaveHub from './pages/MindWaveHub';
import AIStudyAssistant from './pages/AIStudyAssistant';
import SmartCampus from './pages/SmartCampus';
import WellnessCoach from './pages/WellnessCoach';
import AITranslator from './pages/AITranslator';
import Chat from './pages/Chat';
import BookRide from './pages/BookRide';
import Wallet from './pages/Wallet';
import Profile from './pages/Profile';
import Activity from './pages/Activity';
import WhatsAppLogin from './pages/WhatsAppLogin';
import ISACommunity from './pages/ISACommunity';
import DemoMode from './pages/DemoMode';
import LandingPage from './pages/LandingPage';
import Community from './pages/Community';
import ONTAPGlobal from './pages/PeacockConnect';
import CommunityPage from './pages/CommunityPage';
import AnalyticsDashboard from './pages/AnalyticsDashboard';
import AdminDashboard from './pages/AdminDashboard';

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Clean up expired users on app load
    cleanupExpiredUsers();

    // Check if user is logged in using new auth system
    const currentUser = getCurrentUser();
    if (currentUser) {
      setUser(currentUser);
    }
    setLoading(false);
  }, []);

  const handleLogin = (userData, token) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
  };

  const handleLogout = () => {
    logoutUser();
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <Router>
      <div className="App">
        <Navbar user={user} onLogout={handleLogout} />
        <MobileNav user={user} />
        <InstallPrompt />
        <AgenticAIChat />
        <NetworkAccess />
        <Routes>
          {/* ONTAP Global - Main Home */}
          <Route path="/" element={<ONTAPGlobal />} />
          <Route path="/peacock" element={<ONTAPGlobal />} />

          {/* Individual Community Pages */}
          <Route path="/community/:communityId" element={<CommunityPage />} />

          {/* Analytics Dashboard */}
          <Route path="/analytics" element={<AnalyticsDashboard />} />

          {/* Admin Dashboard */}
          <Route path="/admin" element={<AdminDashboard user={user} />} />

          {/* Legacy Pages */}
          <Route path="/welcome" element={<LandingPage />} />
          <Route path="/home" element={<CareemHome />} />
          <Route path="/classic" element={<NewHome />} />

          {/* Auth */}
          <Route
            path="/login"
            element={user ? <Navigate to="/" /> : <Login onLogin={handleLogin} />}
          />
          <Route
            path="/login/whatsapp"
            element={user ? <Navigate to="/" /> : <WhatsAppLogin onLogin={handleLogin} />}
          />
          <Route
            path="/register"
            element={user ? <Navigate to="/" /> : <Register onLogin={handleLogin} />}
          />
          <Route
            path="/verify-magic-link"
            element={<VerifyMagicLink onLogin={handleLogin} />}
          />

          {/* Careem-style Pages */}
          <Route path="/book-ride" element={<BookRide />} />
          <Route path="/wallet" element={<Wallet />} />
          <Route path="/profile" element={<Profile onLogout={handleLogout} />} />
          <Route path="/activity" element={<Activity />} />
          <Route path="/rewards" element={<Activity />} />

          {/* Dashboard */}
          <Route
            path="/dashboard"
            element={user ? <Dashboard user={user} /> : <Navigate to="/login" />}
          />

          {/* Services */}
          <Route path="/services" element={<ServiceList />} />
          <Route path="/services/:id" element={<ServiceDetails user={user} />} />
          <Route path="/marketplace" element={<Marketplace />} />
          <Route path="/rentals" element={<Rentals />} />

          {/* Rides */}
          <Route path="/ride-pooling" element={<RidePooling />} />
          <Route path="/rides" element={<Rides />} />
          <Route
            path="/rides/offer"
            element={user ? <OfferRide /> : <Navigate to="/login" />}
          />

          {/* Social */}
          <Route path="/feed" element={<OnTapFeed />} />
          <Route path="/college-zone" element={<CollegeZone />} />
          <Route path="/community/isa" element={<ISACommunity />} />
          <Route path="/community" element={<Community />} />

          {/* Protected Routes */}
          <Route
            path="/create-service"
            element={user ? <CreateService /> : <Navigate to="/login" />}
          />
          <Route
            path="/my-bookings"
            element={user ? <MyBookings /> : <Navigate to="/login" />}
          />
          <Route
            path="/create-profile"
            element={user ? <CreateProfile /> : <Navigate to="/login" />}
          />

          {/* Showcase & Demo */}
          <Route path="/showcase" element={<Showcase />} />
          <Route path="/demo" element={<DemoMode />} />
          <Route path="/student-tools" element={<StudentTools />} />
          <Route path="/tools/resume-optimizer" element={<ResumeOptimizer />} />
          <Route path="/tools/auto-apply" element={<AutoApply />} />

          {/* AI / MindWave */}
          <Route path="/mindwave" element={<MindWaveHub />} />
          <Route path="/ai/study-assistant" element={<AIStudyAssistant />} />
          <Route path="/ai/career-advisor" element={<ResumeOptimizer />} />
          <Route path="/ai/research-assistant" element={<MindWaveHub />} />
          <Route path="/ai/tutor" element={<AIStudyAssistant />} />
          <Route path="/ai/smart-campus" element={<SmartCampus />} />
          <Route path="/ai/wellness" element={<WellnessCoach />} />
          <Route path="/ai/translator" element={<AITranslator />} />
          <Route path="/ai/writing-assistant" element={<MindWaveHub />} />
          <Route path="/ai/schedule-optimizer" element={<MindWaveHub />} />
          <Route path="/ai/dashboard" element={<MindWaveHub />} />

          {/* Chat */}
          <Route
            path="/chat"
            element={user ? <Chat /> : <Navigate to="/login" />}
          />
          <Route
            path="/chat/:chatId"
            element={user ? <Chat /> : <Navigate to="/login" />}
          />
        </Routes>
      </div>
    </Router>
  );
}

export default App;
